import { useState } from "react";
import { Header, Footer } from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import FlipbookViewer from "../components/FlipbookViewer";
import LiveClassWidget from "../components/LiveClassWidget";
import {
  Download, BookOpen, Layout as LayoutIcon, ExternalLink, Calendar, Clock,
  Slack, Video, GraduationCap, Wifi, Laptop, Mail, MessageSquare, AlertCircle,
  MapPin, Sparkles, Target, CheckCircle2, ChevronRight,
} from "lucide-react";

// The source PDF (used for both download + flipbook embed)
const PDF_URL = "https://customer-assets.emergentagent.com/job_1fa45536-9452-4dc8-b6a3-28317bc1c35c/artifacts/eux8rgvm_Cybersecurity%20Program%20Welcome%20Packet.pdf";

// Color used inside the cosmic hero accent
const GOLD = "#FFD507";

const TEAM_CORE = [
  { name: "Tara May", role: "Aspiritech CEO" },
  { name: "Tanika Edmonds", role: "Workforce Support" },
  { name: "Samantha Kelly", role: "Career Readiness" },
  { name: "Prerana Madhira", role: "Instructor", email: "prerana.madhira@aspiritech.org" },
  { name: "Julia Opalinski", role: "Intake Coordinator" },
];

const TEAM_SUPPORT = [
  { name: "Robin Kacyn", role: "Lead Support", email: "robink@aspiritech.org", primary: true },
  { name: "Brian Sundh", role: "Support" },
  { name: "Doug Norton", role: "Support" },
];

const SMART = [
  { letter: "S", word: "Specific",     body: 'Name your goal clearly. Instead of "I want to do well," try "I want to understand how firewalls work."' },
  { letter: "M", word: "Measurable",   body: "How will you know you've made progress? Pick something concrete — a concept you can explain, a completed task, a certification earned." },
  { letter: "A", word: "Achievable",   body: "Set a goal that fits where you are today. Knowing your needs and limits is a strength, not a setback." },
  { letter: "R", word: "Relevant",     body: "Does this goal move you closer to a cybersecurity career? Think about the job, role, or skill you're working toward." },
  { letter: "T", word: "Time-bound",   body: 'Set a deadline. Pick a specific date — for example, "by the end of week 4."' },
];

const PATHWAY = [
  { num: 1, title: "Weeks 1–8",          body: "Meet virtually Monday–Thursday, 9 AM – 12 PM CT" },
  { num: 2, title: "Week 9",             body: "CompTIA Security+ Exam" },
  { num: 3, title: "Weeks 10–11",        body: "CMMC Training" },
  { num: 4, title: "Week 12",            body: "Career Readiness Training" },
  { num: 5, title: "Work Experience",    body: "& monthly upskilling, individualized per graduate" },
];

const TECH_REQS = [
  "Reliable, unrestricted internet connection",
  "Personal, non-shared laptop or desktop running Windows 11; must be local admin with ability to install software",
  "Operating system, antivirus, and browser all fully updated",
  "Personal Google account (gmail.com) to collaborate in Google Drive and Google Meet",
  "Webcam, microphone, and headphones (or a headset)",
  "A quiet place to work from",
  "Smartphone capable of running Ente Auth, Microsoft Authenticator, Google Authenticator, or Authy for 2FA",
  "A second monitor is helpful, but not required",
];

const Initials = ({ name, accent = "navy" }) => {
  const ch = (name || "?").split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase();
  const bg = accent === "gold" ? "from-[#FFD507] to-[#F59E42]" : "from-[#1E3A8A] to-[#0F1B3D]";
  const txt = accent === "gold" ? "text-[#0F1B3D]" : "text-white";
  return (
    <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${bg} flex items-center justify-center font-heading text-2xl font-bold ${txt} shadow-md mx-auto`}>
      {ch}
    </div>
  );
};

const SectionHeading = ({ id, eyebrow, children }) => (
  <div className="mb-6" id={id}>
    {eyebrow && (
      <span className="text-brand-gold text-xs uppercase tracking-[0.25em] font-bold">{eyebrow}</span>
    )}
    <h2 className="scroll-mt-28 text-3xl sm:text-4xl font-heading font-bold text-slate-900 mt-2">
      {children}
    </h2>
  </div>
);

const TOC = [
  { id: "welcome",     label: "Welcome" },
  { id: "schedule",    label: "Class Schedule" },
  { id: "team",        label: "Meet the Team" },
  { id: "smart",       label: "SMART Goals" },
  { id: "pathway",     label: "Student Pathway" },
  { id: "tech",        label: "Technology" },
  { id: "expectations", label: "Program Expectations" },
  { id: "exam",        label: "Schedule Your Exam" },
  { id: "testing",     label: "Testing Center" },
  { id: "accommodations", label: "Accommodations" },
  { id: "first-day",   label: "Your First Day" },
];

const WelcomePacketCybersecurity = () => {
  const [view, setView] = useState("hub"); // "hub" | "flipbook"

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="welcome-packet-page">
      <Header />

      {/* Cosmic hero */}
      <section
        className="relative overflow-hidden text-white"
        style={{
          background: "radial-gradient(ellipse at 50% 120%, rgba(120, 60, 200, 0.4) 0%, rgba(15, 27, 61, 1) 50%, #060B23 100%)",
        }}
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-50 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(1.5px 1.5px at 40px 40px, rgba(255,255,255,0.7), transparent), " +
              "radial-gradient(1px 1px at 200px 120px, rgba(255,255,255,0.5), transparent), " +
              "radial-gradient(1.5px 1.5px at 380px 220px, rgba(255,255,255,0.6), transparent), " +
              "radial-gradient(1px 1px at 600px 80px, rgba(255,255,255,0.6), transparent), " +
              "radial-gradient(1px 1px at 800px 200px, rgba(255,255,255,0.4), transparent), " +
              "radial-gradient(1.5px 1.5px at 1000px 100px, rgba(255,255,255,0.7), transparent)",
            backgroundSize: "1100px 320px",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-6 py-16 sm:py-20">
          <span className="text-brand-gold text-xs uppercase tracking-[0.3em] font-bold">Welcome Packet</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold mt-3 mb-4 tracking-tight">
            Cybersecurity Cohort
          </h1>
          <p className="text-white/85 max-w-2xl text-base sm:text-lg leading-relaxed">
            You're joining more than a program — you're joining an organization built on the belief
            that neurodivergent talent makes our field stronger. Everything you need to start strong
            lives on this page.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button
              onClick={() => setView("hub")}
              className={`gap-2 ${view === "hub" ? "bg-brand-gold text-brand-navy hover:bg-brand-gold/90" : "bg-white/10 hover:bg-white/20 text-white border border-white/20"}`}
              data-testid="view-toggle-hub"
            >
              <LayoutIcon className="w-4 h-4" /> Web Hub
            </Button>
            <Button
              onClick={() => setView("flipbook")}
              className={`gap-2 ${view === "flipbook" ? "bg-brand-gold text-brand-navy hover:bg-brand-gold/90" : "bg-white/10 hover:bg-white/20 text-white border border-white/20"}`}
              data-testid="view-toggle-flipbook"
            >
              <BookOpen className="w-4 h-4" /> Flipbook
            </Button>
            <a
              href={PDF_URL}
              download="Cybersecurity-Welcome-Packet.pdf"
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white font-medium transition"
              data-testid="download-pdf-btn"
            >
              <Download className="w-4 h-4" /> Download PDF
            </a>
          </div>
        </div>
      </section>

      {/* Flipbook view */}
      {view === "flipbook" && (
        <section className="flex-1 bg-slate-50 py-10" data-testid="flipbook-view">
          <div className="max-w-6xl mx-auto px-6">
            <FlipbookViewer pdfUrl={PDF_URL} />
            <p className="text-xs text-slate-500 text-center mt-4">
              Prefer scrolling?
              <button onClick={() => setView("hub")} className="text-brand-navy hover:underline mx-1">Switch to Web Hub</button>
              for an interactive view.
            </p>
          </div>
        </section>
      )}

      {/* Web Hub view */}
      {view === "hub" && (
        <main className="flex-1 max-w-6xl mx-auto w-full px-6 py-14 grid lg:grid-cols-[220px,1fr] gap-10" data-testid="hub-view">
          {/* Sticky TOC */}
          <nav className="hidden lg:block self-start sticky top-24" aria-label="In this packet">
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-slate-400 mb-3">On this page</p>
            <ul className="space-y-1.5 text-sm">
              {TOC.map(s => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="group flex items-center gap-1.5 text-slate-600 hover:text-brand-navy transition" data-testid={`toc-${s.id}`}>
                    <ChevronRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 text-brand-gold transition" />
                    <span className="group-hover:font-medium">{s.label}</span>
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <article className="space-y-16">
            {/* Welcome */}
            <section>
              <SectionHeading id="welcome" eyebrow="Welcome">A note from the team</SectionHeading>
              <Card className="card-surface">
                <CardContent className="pt-6 pb-6">
                  <p className="text-slate-700 leading-relaxed">
                    We're pleased to welcome you to this cohort. Over the weeks ahead, you'll develop
                    real skills, connect with peers who share your drive, and move closer to a career
                    in cybersecurity. We're here to support you every step of the way.
                  </p>
                  <p className="text-slate-500 italic mt-3">— The Aspiritech Academy Team</p>
                </CardContent>
              </Card>
            </section>

            {/* Class Schedule */}
            <section>
              <SectionHeading id="schedule" eyebrow="Class Schedule">When we meet</SectionHeading>
              <div className="grid sm:grid-cols-2 gap-4">
                <Card className="border-2 border-brand-navy/15 bg-brand-navy/5">
                  <CardContent className="pt-5 pb-5">
                    <div className="flex items-center gap-2 text-brand-navy font-semibold">
                      <Video className="w-4 h-4 text-brand-gold" /> Live Classes
                    </div>
                    <p className="text-2xl font-heading font-bold text-slate-900 mt-2">Mon – Wed</p>
                    <p className="text-slate-600 text-sm mt-1">9:00 AM – 12:00 PM Central</p>
                  </CardContent>
                </Card>
                <Card className="border-2 border-brand-gold/30 bg-brand-gold/5">
                  <CardContent className="pt-5 pb-5">
                    <div className="flex items-center gap-2 text-brand-navy font-semibold">
                      <Clock className="w-4 h-4 text-brand-gold" /> Optional Office Hours
                    </div>
                    <p className="text-2xl font-heading font-bold text-slate-900 mt-2">Thursday</p>
                    <p className="text-slate-600 text-sm mt-1">9:30 AM – 12:00 PM Central</p>
                  </CardContent>
                </Card>
              </div>
            </section>

            {/* Team */}
            <section>
              <SectionHeading id="team" eyebrow="Your People">Meet the team</SectionHeading>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
                {TEAM_CORE.map(m => (
                  <Card key={m.name} className="card-surface text-center" data-testid={`team-${m.name.split(" ")[0].toLowerCase()}`}>
                    <CardContent className="pt-6 pb-5">
                      <Initials name={m.name} />
                      <p className="font-heading font-bold text-slate-900 mt-3 text-sm">{m.name}</p>
                      <p className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{m.role}</p>
                      {m.email && (
                        <a href={`mailto:${m.email}`} className="text-xs text-brand-navy hover:underline mt-1.5 inline-block break-all">
                          {m.email}
                        </a>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
              <SectionHeading eyebrow="Behind the Scenes">Meet the support team</SectionHeading>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {TEAM_SUPPORT.map(m => (
                  <Card key={m.name} className={`${m.primary ? "border-brand-gold/40 bg-brand-gold/5" : "card-surface"}`} data-testid={`support-${m.name.split(" ")[0].toLowerCase()}`}>
                    <CardContent className="pt-5 pb-5 flex items-center gap-4">
                      <Initials name={m.name} accent={m.primary ? "gold" : "navy"} />
                      <div className="text-left">
                        <p className="font-heading font-bold text-slate-900 text-sm">{m.name}</p>
                        <p className="text-[10px] uppercase tracking-wider text-slate-500 mt-0.5">{m.role}</p>
                        {m.email && (
                          <a href={`mailto:${m.email}`} className="text-xs text-brand-navy hover:underline mt-1 inline-block break-all">
                            {m.email}
                          </a>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <Card className="border-brand-navy/20 bg-brand-navy/5 mt-6">
                <CardContent className="pt-5 pb-5 flex items-start gap-3">
                  <Slack className="w-5 h-5 text-brand-navy shrink-0 mt-0.5" />
                  <p className="text-sm text-slate-700 m-0">
                    For any and all support requests, contact
                    <strong className="text-brand-navy"> Robin Kacyn </strong>
                    through <span className="font-semibold">Slack (preferred)</span> or email
                    <a href="mailto:robink@aspiritech.org" className="text-brand-navy hover:underline ml-1">robink@aspiritech.org</a>.
                  </p>
                </CardContent>
              </Card>
            </section>

            {/* SMART Goals */}
            <section>
              <SectionHeading id="smart" eyebrow="Set Yourself Up for Success">SMART goal framework</SectionHeading>
              <p className="text-slate-600 mb-5 leading-relaxed">
                As you begin this program, think intentionally about what you want to achieve — not
                just here, but in your career.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                {SMART.map(s => (
                  <Card key={s.letter} className="card-surface">
                    <CardContent className="pt-5 pb-5">
                      <div className="w-10 h-10 rounded-xl bg-brand-navy text-brand-gold flex items-center justify-center font-heading font-bold text-xl">
                        {s.letter}
                      </div>
                      <p className="font-heading font-bold text-brand-navy mt-3 uppercase text-sm">{s.word}</p>
                      <p className="text-slate-600 text-xs mt-1.5 leading-relaxed">{s.body}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            {/* Pathway */}
            <section>
              <SectionHeading id="pathway" eyebrow="For Illinois Residents">12-week pre-apprenticeship pathway</SectionHeading>
              <div className="space-y-3">
                {PATHWAY.map(p => (
                  <Card key={p.num} className="card-surface" data-testid={`pathway-${p.num}`}>
                    <CardContent className="pt-4 pb-4 flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-brand-gold text-brand-navy font-heading font-bold flex items-center justify-center shrink-0">
                        {p.num}
                      </div>
                      <div>
                        <p className="font-heading font-bold text-slate-900">{p.title}</p>
                        <p className="text-sm text-slate-600">{p.body}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            {/* Tech */}
            <section>
              <SectionHeading id="tech" eyebrow="Get Set Up">Technology you'll use</SectionHeading>
              <div className="grid sm:grid-cols-3 gap-3 mb-6">
                <Card className="card-surface">
                  <CardContent className="pt-5 pb-5">
                    <Slack className="w-6 h-6 text-brand-navy mb-2" />
                    <p className="font-heading font-bold text-slate-900">Slack</p>
                    <p className="text-xs text-slate-500 mt-1">Daily communication</p>
                    <a href="https://slack.com/help/articles/218080037" target="_blank" rel="noreferrer noopener" className="text-xs text-brand-navy hover:underline mt-2 inline-flex items-center gap-1">
                      Getting Started <ExternalLink className="w-3 h-3" />
                    </a>
                  </CardContent>
                </Card>
                <Card className="card-surface">
                  <CardContent className="pt-5 pb-5">
                    <Video className="w-6 h-6 text-brand-navy mb-2" />
                    <p className="font-heading font-bold text-slate-900">Google Meet</p>
                    <p className="text-xs text-slate-500 mt-1">Live virtual classes</p>
                    <a href="https://support.google.com/a/users/answer/9282720" target="_blank" rel="noreferrer noopener" className="text-xs text-brand-navy hover:underline mt-2 inline-flex items-center gap-1">
                      Getting Started <ExternalLink className="w-3 h-3" />
                    </a>
                  </CardContent>
                </Card>
                <Card className="card-surface">
                  <CardContent className="pt-5 pb-5">
                    <GraduationCap className="w-6 h-6 text-brand-navy mb-2" />
                    <p className="font-heading font-bold text-slate-900">LearnWorlds</p>
                    <p className="text-xs text-slate-500 mt-1">Your modules &amp; coursework</p>
                    <span className="text-xs text-slate-400 mt-2 inline-block">Link coming soon</span>
                  </CardContent>
                </Card>
              </div>

              <Card className="card-surface">
                <CardContent className="pt-5 pb-5">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mb-3">For Aspiritech Academy you will need</p>
                  <ul className="grid sm:grid-cols-2 gap-y-2 gap-x-6">
                    {TECH_REQS.map((r, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-brand-gold shrink-0 mt-0.5" />
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </section>

            {/* Expectations */}
            <section>
              <SectionHeading id="expectations" eyebrow="How We Work Together">Program expectations</SectionHeading>
              <div className="grid sm:grid-cols-2 gap-4">
                {[
                  { icon: Calendar, title: "Attendance", body: "Show up for scheduled classes — your presence matters. Communicate any absences to prerana.madhira@aspiritech.org." },
                  { icon: MessageSquare, title: "Engagement", body: "Participate in discussions and activities. Share your thoughts and ask questions." },
                  { icon: CheckCircle2, title: "Task Completion", body: "Complete assignments and tasks on time. We want you building skills, step by step." },
                  { icon: Mail, title: "Communication", body: "Check your email or Slack daily and respond within one business day when we get in touch." },
                  { icon: Clock, title: "Availability", body: "Be available during class times as determined by your instructor (Central Standard Time)." },
                  { icon: Target, title: "Program Completion", body: "You're not just joining classes — you're joining a community that supports you to the finish line." },
                ].map((e, i) => {
                  const I = e.icon;
                  return (
                    <Card key={i} className="card-surface">
                      <CardContent className="pt-5 pb-5 flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-brand-navy/5 flex items-center justify-center shrink-0">
                          <I className="w-5 h-5 text-brand-navy" />
                        </div>
                        <div>
                          <p className="font-heading font-bold text-slate-900">{e.title}</p>
                          <p className="text-sm text-slate-600 mt-0.5">{e.body}</p>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </section>

            {/* Exam */}
            <section>
              <SectionHeading id="exam" eyebrow="CompTIA Security+">How to schedule your exam</SectionHeading>
              <p className="text-slate-600 mb-4 leading-relaxed">
                Below is the full 10-step process. Take your time — there's no rush.
              </p>
              <ol className="space-y-3">
                {[
                  ["Log In", 'Go to the CompTIA login page. Create an account if needed. Click "Schedule Now."'],
                  ["Choose Your Exam", 'Click "View Exams." Select CompTIA Security+.'],
                  ["Confirm Your Information", 'Verify your name matches your ID exactly. Click "Save Updates."'],
                  ["Find Your Exam Version", 'Search for and select "SY0-701." Note: private access code + voucher code.'],
                  ["Select Testing Location", "Decide whether you want to take the exam at a test center or online from your home or office. Ignore any other options given."],
                  ["Read Important Information", "Review all details carefully, especially ID requirements for check-in."],
                  ["Agree to Exam Policies", 'Select your preferred testing language. Review the policy and click "Submit."'],
                  ["Select Date and Time", "For In-person, select College of DuPage, confirm BIC 2A08. For Online, choose a convenient time — double-check AM/PM."],
                  ["Review Your Cart", "Confirm exam date, time, and price. Appointment duration includes check-in time."],
                  ["Complete Payment", "Enter payment details. Illinois residents: enter your Aspiritech voucher code in the designated field."],
                ].map(([title, body], i) => (
                  <li key={i} className="flex gap-4 items-start">
                    <div className="w-8 h-8 rounded-full bg-brand-navy text-brand-gold font-heading font-bold flex items-center justify-center shrink-0">{i+1}</div>
                    <div className="flex-1 pt-1">
                      <p className="font-heading font-bold text-slate-900 text-sm">{title}</p>
                      <p className="text-sm text-slate-600 mt-0.5">{body}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            {/* Testing Center */}
            <section>
              <SectionHeading id="testing" eyebrow="In-Person Testing">College of DuPage testing center</SectionHeading>
              <Card className="border-brand-gold/30 bg-brand-gold/5">
                <CardContent className="pt-5 pb-5 flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-brand-navy shrink-0 mt-0.5" />
                  <div>
                    <p className="font-heading font-bold text-slate-900">Berg Instructional Center (BIC)</p>
                    <p className="text-sm text-slate-700">425 Fawell Blvd<br/>BIC 2A08 — Specialized Testing Services<br/>Glen Ellyn, Illinois 60137</p>
                    <a
                      href="https://maps.google.com/?q=425+Fawell+Blvd+Glen+Ellyn+IL+60137"
                      target="_blank" rel="noreferrer noopener"
                      className="text-xs text-brand-navy hover:underline mt-2 inline-flex items-center gap-1"
                    >
                      Open in Google Maps <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </CardContent>
              </Card>
              <div className="grid sm:grid-cols-2 gap-3 mt-4">
                {[
                  ["Park", "Park anywhere in the lot when you arrive."],
                  ["Find the Landmark", "Walk toward the building. You'll see a tall black triangular fountain in a circular plaza — you're in the right place."],
                  ["Find the Entrance", "Look for the modern building with large windows behind the fountain. The door you need is on the right side."],
                  ["Go Inside", "Enter through the door into the waiting area. Follow the \"Testing Center\" sign on the wall to the right."],
                  ["Lockers", "You cannot bring personal items into the testing room. Store everything in the lockers provided before check-in."],
                ].map(([t, b], i) => (
                  <Card key={i} className="card-surface">
                    <CardContent className="pt-4 pb-4 flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-brand-gold/20 text-brand-navy font-heading font-bold flex items-center justify-center shrink-0 text-sm">{i+1}</div>
                      <div>
                        <p className="font-heading font-bold text-slate-900 text-sm">{t}</p>
                        <p className="text-xs text-slate-600 mt-0.5">{b}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            {/* Accommodations */}
            <section>
              <SectionHeading id="accommodations" eyebrow="Exam Accommodations">Request accommodations through Pearson VUE</SectionHeading>
              <Card className="border-amber-200 bg-amber-50">
                <CardContent className="pt-5 pb-5 flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
                  <p className="text-sm text-amber-900 m-0">
                    <strong>Important:</strong> If you know you'll need exam accommodations,
                    get in touch with Pearson VUE by <strong>week 1 or 2</strong> — accommodation
                    reviews take 10–20 business days.
                  </p>
                </CardContent>
              </Card>
              <ol className="mt-4 space-y-3">
                {[
                  ["Click the CompTIA Accommodations Link", "Pearson VUE reviews and manages all accommodation requests for CompTIA. Click \"Get Started\" at the bottom of the page."],
                  ["Submit Your Request", "First-time users will need to create an account. Have any supporting documentation ready to upload."],
                  ["Wait for Review", "Most requests are reviewed within 10–20 business days."],
                  ["Once Approved", "You'll receive email instructions. Note: you cannot use the standard online scheduler once accommodations are approved."],
                  ["Call (800) 466-0450", "Accommodation scheduling typically requires a phone call to Pearson VUE. Call no less than 15 days before your desired exam date. Or email accommodationspearsonvue@pearson.com."],
                ].map(([t, b], i) => (
                  <li key={i} className="flex gap-4 items-start">
                    <div className="w-8 h-8 rounded-full bg-brand-navy text-brand-gold font-heading font-bold flex items-center justify-center shrink-0">{i+1}</div>
                    <div className="flex-1 pt-1">
                      <p className="font-heading font-bold text-slate-900 text-sm">{t}</p>
                      <p className="text-sm text-slate-600 mt-0.5">{b}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            {/* First day */}
            <section>
              <SectionHeading id="first-day" eyebrow="See You Soon">Your first day of class</SectionHeading>
              <Card className="overflow-hidden">
                <div
                  className="p-6 sm:p-8 text-white"
                  style={{ background: "linear-gradient(135deg, #0F1B3D 0%, #1E3A8A 100%)" }}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-brand-gold/20 flex items-center justify-center shrink-0">
                      <Sparkles className="w-6 h-6" style={{ color: GOLD }} />
                    </div>
                    <div>
                      <p className="text-brand-gold text-xs uppercase tracking-wider font-bold mb-1">Your first class</p>
                      <p className="text-2xl font-heading font-bold">Monday, July 20, 2026</p>
                      <p className="text-white/85 mt-1">9:00 AM Central Standard Time</p>
                      <LiveClassWidget endpoint="/welcome/cybersecurity/live-class" variant="welcome" />
                    </div>
                  </div>
                </div>
              </Card>
              <p className="text-slate-500 italic text-sm mt-4">— The Aspiritech Academy Team</p>
            </section>
          </article>
        </main>
      )}

      <Footer />
    </div>
  );
};

export default WelcomePacketCybersecurity;
